"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

const navLinks = [
  { href: "/homepage", label: "Home" },
  { href: "/infrastructure", label: "Infrastructure" },
  { href: "/agency", label: "Agency" },
  { href: "/pricing", label: "Pricing" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0B0F14]/90 backdrop-blur-xl border-b border-[rgba(0,230,118,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <svg
            viewBox="0 0 260 52"
            width="220"
            height="44"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Scaled-Out Advertising Infrastructure"
          >
            {/* Main wordmark */}
            <text
              x="0"
              y="28"
              fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
              fontWeight="800"
              fontSize="26"
              letterSpacing="1"
              fill="#F0F4F8"
            >
              SCALED-
            </text>
            <text
              x="124"
              y="28"
              fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
              fontWeight="800"
              fontSize="26"
              letterSpacing="1"
              fill="#00E676"
            >
              OUT
            </text>
            {/* Subtitle */}
            <text
              x="1"
              y="44"
              fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
              fontWeight="500"
              fontSize="8.5"
              letterSpacing="3.5"
              fill="#6B7A8D"
            >
              ADVERTISING INFRASTRUCTURE
            </text>
          </svg>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.href}
                href={link?.href}
                className={`text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${
                  isActive
                    ? "text-[#00E676]"
                    : "text-[#6B7A8D] hover:text-[#F0F4F8]"
                }`}
              >
                {link?.label}
              </Link>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          <Link
            href="/contact"
            className="btn-glow btn-glow-pulse px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.12em]"
          >
            Launch Infrastructure →
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={`block w-5 h-0.5 bg-[#F0F4F8] transition-all duration-300 ${
              mobileOpen ? "rotate-45 translate-y-2" : ""
            }`}
          />
          <span
            className={`block w-5 h-0.5 bg-[#F0F4F8] transition-all duration-300 ${
              mobileOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-5 h-0.5 bg-[#F0F4F8] transition-all duration-300 ${
              mobileOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          />
        </button>
      </div>
      {/* Mobile Menu */}
      <div
        className={`md:hidden mobile-menu ${
          mobileOpen ? "visible-menu" : "hidden-menu"
        } bg-[#0B0F14]/98 backdrop-blur-xl border-b border-[rgba(0,230,118,0.08)]`}
      >
        <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.href}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className={`text-sm font-bold uppercase tracking-[0.15em] py-2 border-b border-[rgba(255,255,255,0.04)] transition-colors ${
                  isActive ? "text-[#00E676]" : "text-[#6B7A8D]"
                }`}
              >
                {link?.label}
              </Link>
            );
          })}
          <Link
            href="/contact"
            onClick={() => setMobileOpen(false)}
            className="btn-glow px-6 py-3 rounded-full text-xs font-bold uppercase tracking-[0.12em] text-center mt-2"
          >
            Launch Infrastructure →
          </Link>
        </nav>
      </div>
    </header>
  );
}